import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, Eye, Paperclip } from 'lucide-react';
import { viCall, viOpenFile, inr } from './api';
import { VendorInvoiceDialog } from './VendorInvoiceDialog';
import { OpeningValuePreview } from './OpeningValuePreview';

const STATUS_CLS = { DRAFT: 'bg-amber-100 text-amber-800', POSTED: 'bg-green-100 text-green-800', VOID: 'bg-gray-200 text-gray-600' };

export function VendorInvoicesCard({ token, oid, isSA, onChanged }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    try { setItems((await viCall(`/list/${oid}`, token)).items); } catch (e) { toast.error(e.message); }
  }, [oid, token]);
  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onChanged && onChanged(); };

  return (
    <Card data-testid="vendor-invoices-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Vendor Invoices</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPreview(true)} data-testid="btn-opening-value-preview"><Eye className="w-4 h-4 mr-1" />Opening Values</Button>
          <Button size="sm" onClick={() => setEditing({})} data-testid="btn-add-vendor-invoice"><Plus className="w-4 h-4 mr-1" />Add Vendor Invoice</Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">Upload each supplier bill, match its lines to PO lines, and post it. Posting records received qty and replaces the estimated rate with the actual billed rate in the opening registers.</p>
        {items.length === 0 ? <div className="text-xs text-muted-foreground" data-testid="vendor-invoices-empty">No vendor invoices yet.</div> : (
          <table className="w-full text-xs">
            <thead className="text-left border-b"><tr><th className="p-1">Vendor</th><th>Invoice #</th><th>Date</th><th>PO</th><th>Lines</th><th className="text-right">Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map(v => (
                <tr key={v.id} className="border-b hover:bg-muted/40" data-testid={`vi-row-${v.id}`}>
                  <td className="p-1">{v.vendor}</td>
                  <td className="font-mono">{v.invoice_number}</td>
                  <td>{v.invoice_date || '—'}</td>
                  <td className="font-mono">{v.po_number || '—'}</td>
                  <td>{v.lines.length}</td>
                  <td className="text-right">{inr(v.totals?.final_amount)}</td>
                  <td><Badge className={`text-[10px] ${STATUS_CLS[v.status]}`} data-testid={`vi-status-${v.id}`}>{v.status}</Badge></td>
                  <td className="text-right whitespace-nowrap">
                    {v.file_path && <Button size="sm" variant="ghost" onClick={() => viOpenFile(token, v.id).catch(e => toast.error(e.message))} data-testid={`vi-file-${v.id}`}><Paperclip className="w-3 h-3" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => setEditing(v)} data-testid={`vi-open-${v.id}`}>{v.status === 'DRAFT' ? 'Edit' : 'View'}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {editing && <VendorInvoiceDialog token={token} oid={oid} isSA={isSA} invoice={editing} onClose={() => setEditing(null)} onSaved={load} onChanged={changed} />}
      {preview && <OpeningValuePreview token={token} oid={oid} onClose={() => setPreview(false)} />}
    </Card>
  );
}
